/**
 * THE THEME — dark by default, light for daylight (founder-ruled 2026-08-27).
 *
 * ── ⚠ THIS REVERSES "DARK ONLY", AND THE REASON IS PHYSICS ───────────────────────────────────
 * `tokens.css` carried *"DARK ONLY — a decision, not an omission (founder, 2026-08-14)"*, on the
 * ground that a light Heimar would be a different product. That instinct was right about the
 * *affect* and wrong about one thing nobody had measured: **the palette is already excellent and
 * still fails in sunlight, and no palette change can fix it.**
 *
 * The dark theme's contrast is AAA on body ink (12.2 : 1). Sunlight failure is not a contrast
 * problem — it is a *luminance* problem. Bright ambient light lays a reflected glare floor across
 * the whole screen; a light background out-emits that floor and stays readable, while a dark
 * background **cannot go below it**, so 12 : 1 collapses toward 1 : 1 and the type disappears. It
 * is why every e-reader and every navigation app that cares goes light outdoors, whatever its
 * house style.
 *
 * Founder, 2026-08-27: *"i love the dark theme… but it does seem like its way more difficult to
 * read in the sunlight on your phone."* Both halves of that are true, which is why this is a
 * choice rather than a replacement.
 *
 * ── WHAT LIGHT IS HERE, AND WHAT IT IS NOT ──────────────────────────────────────────────────
 * ⚠ **NOT AN INVERSION.** A mechanically inverted Heimar is the "boring" the founder feared, and
 * the risk was real. The light palette is designed as **paper** — a warm off-white ground, warm
 * near-black ink, the same brass darkened until it holds — so it reads as the material the reading
 * happens on rather than as the absence of the dark one. It mirrors the dark palette's contrasts
 * closely enough to be the same product in daylight (`ink` 11.4 against 12.2).
 *
 * ── HOW IT RESOLVES ─────────────────────────────────────────────────────────────────────────
 * `data-theme` on `<html>`, and there are only two values — the reader has either chosen or not.
 * Unset means **follow the system**, which is the honest default: somebody who has told their
 * phone they want light at noon has already answered this question once.
 *
 * ⚠ SET BEFORE FIRST PAINT by an inline script in `index.html`, not by React. A reader in sunlight
 * who gets a dark flash before the light theme lands has been shown precisely the thing they
 * cannot read, at the moment they cannot read it.
 *
 * ⚠ AND ANY SURFACE MAY PIN ITSELF. `data-theme` is an attribute, so a screen with a reason of its
 * own can set it on its own root — the "dark hall, lit page" idea (an atmospheric shelf over a lit
 * reading surface) needs no new mechanism, only the decision. Not taken; available.
 */

export type Theme = 'dark' | 'light'

/** Unset = follow the system. Two values, because the reader has either chosen or not. */
export type ThemeChoice = Theme | 'system'

const KEY = 'heimar.theme'

export function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'dark' || v === 'light' ? v : 'system'
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return 'system'
  }
}

/** What the system asks for. Dark when it has no opinion — the house style is the fallback. */
export function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

export const resolveTheme = (choice: ThemeChoice = readChoice()): Theme =>
  choice === 'system' ? systemTheme() : choice

/**
 * Write the choice and apply it.
 *
 * ⚠ IT ALSO MOVES `theme-color`, which is the browser chrome around the page — the address bar on
 * Android, the status area in a standalone PWA. Leaving it at the dark ground would frame a paper
 * page in near-black, which is worse than either theme on its own.
 */
export function applyTheme(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    /* a preference that cannot be stored is still worth applying for this visit */
  }

  const resolved = resolveTheme(choice)
  const root = document.documentElement

  // `system` leaves the attribute off entirely, so the media query in `tokens.css` governs — which
  // is also what makes the no-JS path correct rather than merely tolerable.
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'light' ? '#f3eee4' : '#050505')
}

/**
 * Follow the system while the reader has expressed no preference of their own.
 *
 * Somebody whose phone switches at dusk should switch with it, without having been asked to hold
 * an opinion about it. Returns its own teardown.
 */
export function watchSystemTheme(): () => void {
  try {
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = () => {
      if (readChoice() === 'system') applyTheme('system')
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  } catch {
    return () => {}
  }
}
